import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { apiFetch, v1Path } from '../../lib/api';
import { useOverlays } from './overlay-context';
import BlockedReasonDialog, { type BlockedSubmit } from './BlockedReasonDialog';

type MoveState = {
  id: string;
  name: string;
};

type Props = {
  projectId: string;
  workItemId: string;
  workItemKey: string;
  version: number;
  children: React.ReactNode;
  onChanged?: () => void;
  /** Called after a successful delete so the list can drop the row immediately. */
  onRemoved?: (workItemId: string) => void;
  states?: MoveState[];
  currentStateId?: string;
  /** Pass to offer Mark blocked / Unblock; null when the item is not blocked. */
  blockedReason?: string | null;
  /** Initiatives are archived. Everything else is marked done. */
  itemType?: string;
};

export default function ItemContextMenu({
  projectId,
  workItemId,
  workItemKey,
  version,
  children,
  onChanged,
  onRemoved,
  states,
  currentStateId,
  blockedReason,
  itemType,
}: Props) {
  const [, setSearchParams] = useSearchParams();
  const { toast, confirm } = useOverlays();
  const [blockOpen, setBlockOpen] = useState(false);

  function openPeek() {
    setSearchParams((curr) => {
      const next = new URLSearchParams(curr);
      next.set('peek', workItemKey);
      return next;
    });
  }

  function openFull() {
    window.open(`/browse/${encodeURIComponent(workItemKey)}`, '_blank', 'noopener,noreferrer');
  }

  function copyLink() {
    const url = `${window.location.origin}/browse/${workItemKey}`;
    void navigator.clipboard.writeText(url).then(
      () => toast('Link copied'),
      () => toast('Could not copy link'),
    );
  }

  function forgetPeek() {
    setSearchParams((curr) => {
      if (curr.get('peek') !== workItemKey) return curr;
      const next = new URLSearchParams(curr);
      next.delete('peek');
      return next;
    });
  }

  async function moveTo(stateId: string) {
    if (!states?.some((state) => state.id === stateId)) return;
    if (stateId === currentStateId) return;
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${workItemId}/move`), {
        method: 'POST',
        body: JSON.stringify({ version, state_id: stateId }),
      });
      toast('Moved');
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      onChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Move failed');
    }
  }

  async function setBlocked(next: BlockedSubmit | null) {
    const body = next
      ? { version, blocked_reason: next.reason, blocked_by_ids: next.blockedByIds }
      : { version, blocked_reason: null };
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${workItemId}`), {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      toast(next ? 'Marked blocked' : 'Unblocked');
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      onChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Update failed');
      throw err;
    }
  }

  const markingDone = itemType !== 'initiative';

  async function archive() {
    const ok = await confirm({
      title: markingDone ? 'Mark as done?' : 'Archive work item',
      description: markingDone ? `${workItemKey} will show on Completed.` : `Archive ${workItemKey}?`,
      confirmLabel: markingDone ? 'Mark done' : 'Archive',
    });
    if (!ok) return;
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${workItemId}/archive`), {
        method: 'POST',
        body: JSON.stringify({ version }),
      });
      toast(markingDone ? 'Marked done' : 'Archived');
      onChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not mark it done');
    }
  }

  async function remove() {
    const ok = await confirm({
      title: 'Delete work item',
      description: `Delete ${workItemKey}? This cannot be undone from the list.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${workItemId}`), {
        method: 'DELETE',
      });
      toast('Deleted');
      forgetPeek();
      onRemoved?.(workItemId);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      onChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={openPeek}>Open peek</DropdownMenuItem>
          <DropdownMenuItem onSelect={openFull}>Open full page</DropdownMenuItem>
          {states && states.length > 0 ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Move to…</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {states.map((state) => (
                  <DropdownMenuItem
                    key={state.id}
                    disabled={state.id === currentStateId}
                    onSelect={() => void moveTo(state.id)}
                  >
                    {state.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : null}
          {blockedReason === undefined ? null : blockedReason ? (
            <DropdownMenuItem onSelect={() => void setBlocked(null).catch(() => undefined)}>Unblock</DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => setBlockOpen(true)}>Mark blocked…</DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={copyLink}>Copy link</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void archive()}>{markingDone ? 'Mark done' : 'Archive'}</DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => void remove()}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {blockedReason === undefined ? null : (
        <BlockedReasonDialog
          open={blockOpen}
          onOpenChange={setBlockOpen}
          workItemKey={workItemKey}
          projectId={projectId}
          onSubmit={(next) => setBlocked(next)}
        />
      )}
    </>
  );
}
